import { useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack, Typography } from "@mui/material";
import AuthInput from "../../components/Input/AuthInput";
import { DamsColors } from "../../components/styles/DamsColors";
import { ConfirmActionModal } from "../../components/Modals/ActionModal";
import { changePassword, ChangePasswordRequest } from "../../services/auth/changePassword";
import { FormField, defaultValue } from "../../utils/formSchema";
import { useFieldHasError } from "../../hooks/useFieldHasError";

interface ChangePasswordForm extends FormField<{
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}> {
  submitAttempt?: boolean;
}

function ChangePasswordPage() {
  const [form, updateForm] = useState<ChangePasswordForm>({
    currentPassword: { ...defaultValue },
    newPassword: { ...defaultValue },
    confirmPassword: { ...defaultValue }
  });
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiError, setApiError] = useState<string>("");

  const fieldHasError = useFieldHasError<ChangePasswordForm>(form);

  // password validation function
  const validatePassword = (password: string): { isValid: boolean; errorMessage?: string } => {
    if (password.length === 0) {
      return { isValid: false, errorMessage: "Password is required" };
    }
    if (password.length < 8) {
      return { isValid: false, errorMessage: "Password must be at least 8 characters" };
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return { isValid: false, errorMessage: "Password must contain at least 1 lowercase letter" };
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return { isValid: false, errorMessage: "Password must contain at least 1 uppercase letter" };
    }
    if (!/(?=.*\d)/.test(password)) {
      return { isValid: false, errorMessage: "Password must contain at least 1 number" };
    }
    return { isValid: true };
  };

  // confirm password validation
  const validateConfirmPassword = (confirmPassword: string, newPassword: string): { isValid: boolean; errorMessage?: string } => {
    if (confirmPassword.length === 0) {
      return { isValid: false, errorMessage: "Please confirm your password" };
    }
    if (confirmPassword !== newPassword) {
      return { isValid: false, errorMessage: "Passwords do not match" };
    }
    return { isValid: true };
  };

  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateForm(prev => ({
      ...prev,
      currentPassword: {
        value,
        isValid: value.length > 0,
        errorMessage: value.length === 0 ? "Current password is required" : undefined
      }
    }));
    setApiError("");
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = validatePassword(value);
    
    updateForm(prev => ({
      ...prev,
      newPassword: {
        value,
        isValid: validation.isValid,
        errorMessage: validation.errorMessage
      },
      // re-validate confirm password if it exists
      confirmPassword: prev.confirmPassword.value ? {
        ...prev.confirmPassword,
        ...validateConfirmPassword(prev.confirmPassword.value, value)
      } : prev.confirmPassword
    }));
    setApiError("");
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validation = validateConfirmPassword(value, form.newPassword.value);
    
    updateForm(prev => ({
      ...prev,
      confirmPassword: {
        value,
        isValid: validation.isValid,
        errorMessage: validation.errorMessage
      }
    }));
    setApiError("");
  };

  // resets all form fields
  const resetForm = () => {
    updateForm({
      currentPassword: { ...defaultValue },
      newPassword: { ...defaultValue },
      confirmPassword: { ...defaultValue }
    });
    setApiError("");
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = () => {
    updateForm(prev => ({ ...prev, submitAttempt: true }));
    
    // check if all fields are valid
    if (form.currentPassword.isValid && 
        form.newPassword.isValid && 
        form.confirmPassword.isValid) {
      setShowConfirmModal(true);
    }
  };

  const confirmPasswordChange = async () => {

    setIsProcessing(true);
    setApiError("");

    try {
      const requestData: ChangePasswordRequest = {
        current_password: form.currentPassword.value,
        new_password: form.newPassword.value
      };

      const response = await changePassword(requestData);
      
    
      
      alert(response.message || "Password changed successfully!");
      
      resetForm();
      setShowConfirmModal(false);
      
    } catch (error: any) {
 
      
      let errorMessage = "Failed to change password. Please try again.";
      
      if (error.response) {
        if (error.response.status === 422) {
          const validationErrors = error.response.data?.detail;
          if (validationErrors && validationErrors.length > 0) {
            errorMessage = validationErrors[0].msg || errorMessage;
          }
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || "Current password is incorrect";
        } else if (error.response.status === 401) {
          errorMessage = "Session expired. Please log in again.";
        } else {
          errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  return (
    <MainPageBase
      pageState="add-item"
      title="Change Password"
      cancelBtnClick={handleCancel}
      addItemBtnOnClick={handleSave}
    >
      <Box
        height={`100%`}
        display={"flex"}
        justifyContent={"center"}
      >
        <Stack width={"100%"}>
          <Box mb={3} display={"flex"} justifyContent={"space-between"}>
            <Stack direction={"column"} width={500} spacing={2}>
              <AuthInput
                label="Current Password"
                placeholder="Enter current password"
                type="password"
                value={form.currentPassword.value}
                onChange={handleCurrentPasswordChange}
                error={fieldHasError('currentPassword')}
                errorMessage={form.currentPassword.errorMessage}
              />
              <AuthInput
                label="New Password"
                placeholder="Enter new password"
                type="password"
                value={form.newPassword.value}
                onChange={handleNewPasswordChange}
                error={fieldHasError('newPassword')}
                errorMessage={form.newPassword.errorMessage}
              />
              <AuthInput
                label="Confirm New Password"
                placeholder="Confirm new password"
                type="password"
                value={form.confirmPassword.value}
                onChange={handleConfirmPasswordChange}
                error={fieldHasError('confirmPassword')}
                errorMessage={form.confirmPassword.errorMessage}
              />
            </Stack>
          </Box>

          {/* api error display */}
          {apiError && (
            <Box mb={2} width={500}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={500}
                fontSize={14}
                color="error"
                sx={{ 
                  backgroundColor: "#FEF2F2", 
                  padding: "8px 12px", 
                  borderRadius: "4px",
                  border: "1px solid #FECACA"
                }}
              >
                {apiError}
              </Typography>
            </Box>
          )}

          <Box mt={2} mb={3} width={500}>
            <Typography
              fontFamily={"Inter"}
              fontWeight={500}
              fontSize={14}
              color={DamsColors.Authentication.InputLabel}
            >
              Password must contain:
            </Typography>
            <ul
              style={{
                marginTop: 8,
                marginBottom: 20,
                paddingLeft: 20,
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: 14,
                color: "#344054",
                listStyleType: "disc",
                lineHeight: "1.8em",
              }}
            >
              <li>A minimum of 8 characters</li>
              <li>At least 1 uppercase letter</li>
              <li>At least 1 lowercase letter</li>
              <li>At least 1 number</li>
            </ul>
          </Box>
        </Stack>
      </Box>

      {/* Confirmation Modal */}
      <ConfirmActionModal
        show={showConfirmModal}
        title={"Change Password"}
        subtitle={`Are you sure you want to change your password? Click 'Confirm' to continue.`}
        onRightBtnPress={confirmPasswordChange}
        onRightBtnLoading={isProcessing}
        rightBtnTxt={"Confirm"}
        onLeftBtnPress={closeConfirmModal}
        onClosePress={closeConfirmModal}
        leftBtnTxt={"Cancel"}
      />
    </MainPageBase>
  );
}

export default ChangePasswordPage;